import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, IndianRupee, Loader2, MailWarning, ShoppingCart, Timer, TrendingUp } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const inputCls = "h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none";
const STATUS_CHIPS = [
  { key: "all", label: "All" },
  { key: "abandoned", label: "Abandoned" },
  { key: "recovered", label: "Recovered" },
];

function fmtDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminAbandoned() {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(() => {
    const params = { status, page, page_size: 20 };
    if (from) params.from = from;
    if (to) params.to = to;
    api.get("/admin/abandoned-carts", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load abandoned carts")));
    const kp = { status };
    if (from) kp.from = from;
    if (to) kp.to = to;
    api.get("/admin/abandoned-carts/kpis", { params: kp }).then(({ data }) => setKpis(data)).catch(() => {});
  }, [status, from, to, page]);
  useEffect(load, [load]);

  useEffect(() => {
    api.get("/admin/abandoned-carts/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/abandoned-carts/settings", settings);
      setSettings(data);
      toast.success(data.reminder_enabled
        ? "Settings saved — reminder emails are ON (one per abandoned cart)"
        : "Settings saved — reminder emails stay OFF");
    } catch (e) {
      toast.error(apiError(e, "Couldn't save settings"));
    } finally {
      setSavingSettings(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div data-testid="admin-abandoned" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
          <ShoppingCart className="h-6 w-6 text-amber-400" /> Abandoned Carts
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Signed-in carts that went idle. Customers restore them at current prices — nothing is ordered or discounted automatically.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="abandoned-kpis">
        {[
          { label: "Abandoned Carts", value: kpis?.abandoned_carts ?? "—", icon: ShoppingCart, testid: "kpi-abandoned-count" },
          { label: "Abandoned Value", value: kpis ? fmt(kpis.abandoned_value) : "—", icon: IndianRupee, testid: "kpi-abandoned-value" },
          { label: "Recovered Carts", value: kpis?.recovered_carts ?? "—", icon: TrendingUp, testid: "kpi-recovered-count" },
          { label: "Recovered Value", value: kpis ? fmt(kpis.recovered_value) : "—", icon: IndianRupee, testid: "kpi-recovered-value" },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-4" data-testid={k.testid}>
            <k.icon className="h-4 w-4 text-amber-500" />
            <p className="mt-3 text-xl font-bold text-white">{k.value}</p>
            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="abandoned-filters">
        {STATUS_CHIPS.map((c) => (
          <button key={c.key} onClick={() => { setStatus(c.key); setPage(1); }}
            data-testid={`filter-status-${c.key}`}
            className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
              status === c.key ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:border-white/25"}`}>
            {c.label}
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-white/10 sm:block" />
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }}
          data-testid="filter-from" className={inputCls} aria-label="From date" />
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }}
          data-testid="filter-to" className={inputCls} aria-label="To date" />
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-[#FFFEFA]" data-testid="abandoned-table">
        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
        ) : data.records.length === 0 ? (
          <p className="py-16 text-center text-sm text-neutral-500" data-testid="abandoned-empty">No abandoned carts match these filters.</p>
        ) : (
          data.records.map((r) => (
            <div key={r.id} className="border-b border-white/[0.05] last:border-0" data-testid={`abandoned-row-${r.id}`}>
              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                data-testid={`abandoned-expand-${r.id}`}
                className="grid w-full grid-cols-2 items-center gap-2 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.02] sm:grid-cols-7 sm:gap-3">
                <span className="flex min-w-0 items-center gap-2 sm:col-span-2">
                  {expanded === r.id ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-500" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-neutral-500" />}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-white" data-testid={`abandoned-customer-${r.id}`}>{r.customer?.name || "—"}</span>
                    <span className="block truncate text-[11px] text-neutral-500">{r.customer?.email}</span>
                  </span>
                </span>
                <span className="text-xs font-bold text-white" data-testid={`abandoned-value-${r.id}`}>{fmt(r.value_at_abandonment)}</span>
                <span className="text-xs text-neutral-400">{r.item_count} item{r.item_count === 1 ? "" : "s"}</span>
                <span className="text-xs text-neutral-400">{fmtDate(r.abandoned_at)}</span>
                <span className="text-xs text-neutral-400" data-testid={`abandoned-current-${r.id}`}>{fmt(r.current_cart_value)}</span>
                <span className={`justify-self-start rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                  r.status === "recovered" ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400"}`}
                  data-testid={`abandoned-status-${r.id}`}>
                  {r.status}
                </span>
              </button>
              {expanded === r.id && (
                <div className="grid gap-4 border-t border-white/[0.05] bg-black/30 px-5 py-4 sm:grid-cols-2" data-testid={`abandoned-detail-${r.id}`}>
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Cart at abandonment</p>
                    <div className="space-y-1.5">
                      {r.items.map((it) => (
                        <p key={`${it.product_id}-${it.sku}`} className="text-xs text-neutral-300">
                          <span className="font-bold text-white">{it.name || it.product_id}</span>
                          {it.variant_label ? <span className="text-neutral-500"> · {it.variant_label}</span> : null}
                          <span className="text-neutral-500"> × {it.qty}</span>
                          {it.unit_price != null && <span className="text-neutral-500"> @ ₹{Number(it.unit_price).toLocaleString("en-IN")}</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                      Timeline{r.recovered_at ? ` · recovered ${fmtDate(r.recovered_at)} (${r.recovery_via})` : ""}
                    </p>
                    <div className="space-y-1.5">
                      {(r.events || []).map((ev, i) => (
                        <p key={i} className="flex items-start gap-2 text-xs text-neutral-400" data-testid={`abandoned-event-${r.id}-${i}`}>
                          <Timer className="mt-0.5 h-3 w-3 shrink-0 text-neutral-600" />
                          <span><span className="font-bold capitalize text-neutral-200">{ev.type.replace(/_/g, " ")}</span>
                            {" "}· {fmtDate(ev.at)}{ev.details ? <span className="text-neutral-500"> — {ev.details}</span> : null}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="abandoned-prev"
            className="rounded-lg border border-white/10 px-4 py-2 text-xs font-bold text-neutral-300 disabled:opacity-40">Prev</button>
          <span className="text-xs text-neutral-500">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="abandoned-next"
            className="rounded-lg border border-white/10 px-4 py-2 text-xs font-bold text-neutral-300 disabled:opacity-40">Next</button>
        </div>
      )}

      {settings && (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-5" data-testid="abandoned-settings">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
            <MailWarning className="h-4 w-4 text-amber-400" /> Detection & Reminders
          </h2>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Mark abandoned after (hours)
              </label>
              <input type="number" min="1" max="720" value={settings.inactivity_hours}
                onChange={(e) => setSettings((s) => ({ ...s, inactivity_hours: parseInt(e.target.value) || 1 }))}
                data-testid="settings-inactivity-hours" className={`${inputCls} w-32`} />
            </div>
            <button onClick={() => setSettings((s) => ({ ...s, reminder_enabled: !s.reminder_enabled }))}
              role="switch" aria-checked={settings.reminder_enabled} data-testid="settings-reminder-toggle"
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${settings.reminder_enabled ? "bg-amber-500" : "bg-white/10"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${settings.reminder_enabled ? "left-6" : "left-1"}`} />
            </button>
            <div className="pb-1">
              <p className="text-xs font-bold text-white">Reminder emails {settings.reminder_enabled ? "ON" : "OFF"}</p>
              <p className="mt-0.5 max-w-sm text-[11px] leading-relaxed text-neutral-500">
                When on, a customer gets at most ONE gentle reminder per abandoned cart — never if they already came back.
                Kept off by default.
              </p>
            </div>
            <button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save-btn"
              className="flex h-10 items-center gap-2 rounded-xl bg-amber-500 px-5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
              {savingSettings && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
